import { useState, useEffect, useRef } from 'react';
import api from '../api/axios';

const styles = {
  container: {
    padding: '20px',
    maxWidth: '800px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
  },
  subtitle: {
    fontSize: '13px',
    color: 'var(--text-muted)',
    marginTop: '4px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '12px',
    marginBottom: '32px',
  },
  statCard: {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--radius-md)',
    padding: '16px',
    textAlign: 'center',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: '700',
    color: 'var(--accent)',
  },
  statLabel: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginTop: '4px',
  },
  section: {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--radius-lg)',
    padding: '24px',
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '8px',
  },
  sectionDesc: {
    fontSize: '13px',
    color: 'var(--text-secondary)',
    marginBottom: '16px',
    lineHeight: 1.5,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  input: {
    width: '80px',
    padding: '8px 12px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-color)',
    background: 'var(--bg-elevated)',
    color: 'var(--text-primary)',
    fontSize: '14px',
    textAlign: 'center',
  },
  log: {
    marginTop: '16px',
    padding: '12px',
    background: 'var(--bg-elevated)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '13px',
    lineHeight: 1.6,
    maxHeight: '200px',
    overflowY: 'auto',
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
  },
  logSuccess: { color: 'var(--success)' },
  logError: { color: 'var(--danger)' },
  logInfo: { color: 'var(--text-secondary)' },
  fileInput: {
    display: 'none',
  },
  fileLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    borderRadius: 'var(--radius-sm)',
    border: '1px dashed var(--border-color)',
    cursor: 'pointer',
    fontSize: '13px',
    color: 'var(--text-secondary)',
    transition: 'all 200ms',
  },
  modeToggle: {
    display: 'flex',
    gap: '0',
    borderRadius: 'var(--radius-sm)',
    overflow: 'hidden',
    border: '1px solid var(--border-color)',
  },
  modeBtn: {
    padding: '8px 16px',
    fontSize: '13px',
    border: 'none',
    cursor: 'pointer',
    background: 'var(--bg-elevated)',
    color: 'var(--text-secondary)',
    transition: 'all 200ms',
  },
  modeBtnActive: {
    background: 'var(--accent)',
    color: '#fff',
  },
  dangerSection: {
    borderColor: 'rgba(232, 87, 109, 0.3)',
  },
};

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [seedCount, setSeedCount] = useState(100);
  const [loading, setLoading] = useState('');
  const [logs, setLogs] = useState([]);
  const [importMode, setImportMode] = useState('merge');
  const [importFile, setImportFile] = useState(null);
  const fileRef = useRef(null);

  const addLog = (message, type = 'info') => {
    setLogs((prev) => [...prev, { message, type, time: new Date().toLocaleTimeString() }]);
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/stats');
      setStats(res.data);
    } catch (err) {
      addLog('Failed to fetch stats: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleSeed = async () => {
    setLoading('seed');
    addLog(`Seeding ${seedCount} test users...`);
    try {
      const res = await api.post('/admin/seed', { count: seedCount });
      addLog(res.data.message, 'success');
      if (res.data.sample?.length > 0) {
        addLog(`  Sample: ${res.data.sample[0].email} / TestPass123!`, 'info');
      }
      await fetchStats();
    } catch (err) {
      addLog('Seed failed: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setLoading('');
    }
  };

  const handleDownloadFaces = async () => {
    setLoading('faces');
    addLog('Downloading profile photos from randomuser.me for seed users...');
    addLog('  This should take under a minute for 100 users', 'info');
    try {
      const res = await api.post('/admin/download-faces', {}, { timeout: 300000 });
      addLog(res.data.message, 'success');
      await fetchStats();
    } catch (err) {
      addLog('Download failed: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setLoading('');
    }
  };

  const handleClearSeed = async () => {
    setLoading('clear');
    addLog('Removing all seed users...');
    try {
      const res = await api.delete('/admin/seed');
      addLog(res.data.message, 'success');
      await fetchStats();
    } catch (err) {
      addLog('Clear failed: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setLoading('');
    }
  };

  const handleExport = async (seedOnly = false) => {
    setLoading('export');
    addLog(`Exporting ${seedOnly ? 'seed' : 'all'} users...`);
    try {
      const res = await api.get(`/admin/export?seedOnly=${seedOnly}`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `novadate-${seedOnly ? 'seed' : 'full'}-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      addLog(`Exported ${res.data.count} users`, 'success');
    } catch (err) {
      addLog('Export failed: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setLoading('');
    }
  };

  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImportFile(file);
      addLog(`Selected file: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`, 'info');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      addLog('No file selected', 'error');
      return;
    }

    setLoading('import');
    addLog(`Importing users (${importMode} mode)...`);
    try {
      const text = await importFile.text();
      const data = JSON.parse(text);

      if (!data.users || !Array.isArray(data.users)) {
        addLog('Invalid file format: missing "users" array', 'error');
        setLoading('');
        return;
      }

      addLog(`  Found ${data.users.length} users in file`, 'info');

      const res = await api.post('/admin/import', {
        users: data.users,
        mode: importMode,
      });

      addLog(res.data.message, 'success');
      setImportFile(null);
      if (fileRef.current) fileRef.current.value = '';
      await fetchStats();
    } catch (err) {
      addLog('Import failed: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setLoading('');
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Admin Panel</h1>
          <p style={styles.subtitle}>Manage test data and user database</p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.total_users}</div>
            <div style={styles.statLabel}>Total Users</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.seed_users}</div>
            <div style={styles.statLabel}>Seed Users</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.real_users}</div>
            <div style={styles.statLabel}>Real Users</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.photos}</div>
            <div style={styles.statLabel}>Photos</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.matches}</div>
            <div style={styles.statLabel}>Matches</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.traits}</div>
            <div style={styles.statLabel}>Traits</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.preferences}</div>
            <div style={styles.statLabel}>Preferences</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.messages}</div>
            <div style={styles.statLabel}>Messages</div>
          </div>
        </div>
      )}

      {/* Seed Users */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Seed Test Users</h2>
        <p style={styles.sectionDesc}>
          Generate random test users with complete profiles — names, photos, traits, preferences,
          vaccinations, and prompt responses. All located in Texas cities (concentrated in Austin & Houston).
          Password for all seed accounts: <strong>TestPass123!</strong>
        </p>
        <div style={styles.row}>
          <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Number of users:</span>
          <input
            type="number"
            value={seedCount}
            onChange={(e) => setSeedCount(Math.max(1, Math.min(1000, parseInt(e.target.value) || 1)))}
            style={styles.input}
            min={1}
            max={1000}
          />
          <button
            className="btn btn-primary"
            onClick={handleSeed}
            disabled={!!loading}
          >
            {loading === 'seed' ? 'Seeding...' : 'Seed Users'}
          </button>
        </div>
      </div>

      {/* Face Photos */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Profile Photos</h2>
        <p style={styles.sectionDesc}>
          Download gender-matched profile photos from randomuser.me for all seed users,
          replacing the default geometric avatars. Skips users that already have face photos.
        </p>
        <div style={styles.row}>
          <button
            className="btn btn-primary"
            onClick={handleDownloadFaces}
            disabled={!!loading}
          >
            {loading === 'faces' ? 'Downloading faces...' : 'Download Face Photos'}
          </button>
        </div>
      </div>

      {/* Export */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Export Database</h2>
        <p style={styles.sectionDesc}>
          Download the user database as a JSON file. You can export all users or just seed (test) users.
          The export includes all profile data, traits, preferences, vaccinations, and prompts.
        </p>
        <div style={styles.row}>
          <button
            className="btn btn-secondary"
            onClick={() => handleExport(false)}
            disabled={!!loading}
          >
            {loading === 'export' ? 'Exporting...' : 'Export All Users'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => handleExport(true)}
            disabled={!!loading}
          >
            Export Seed Users Only
          </button>
        </div>
      </div>

      {/* Import */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Import Database</h2>
        <p style={styles.sectionDesc}>
          Upload a previously exported JSON file to restore users.
          Choose <strong>Merge</strong> to add new users alongside existing ones (skips duplicates),
          or <strong>Overwrite</strong> to replace the entire database.
        </p>

        <div style={{ marginBottom: '16px' }}>
          <span style={{ fontSize: '13px', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
            Import mode:
          </span>
          <div style={styles.modeToggle}>
            <button
              style={{ ...styles.modeBtn, ...(importMode === 'merge' ? styles.modeBtnActive : {}) }}
              onClick={() => setImportMode('merge')}
            >
              Merge
            </button>
            <button
              style={{ ...styles.modeBtn, ...(importMode === 'overwrite' ? styles.modeBtnActive : {}) }}
              onClick={() => setImportMode('overwrite')}
            >
              Overwrite
            </button>
          </div>
        </div>

        <div style={styles.row}>
          <label style={styles.fileLabel} htmlFor="import-file">
            {importFile ? `${importFile.name}` : 'Choose JSON file...'}
          </label>
          <input
            id="import-file"
            ref={fileRef}
            type="file"
            accept=".json"
            onChange={handleImportFile}
            style={styles.fileInput}
          />
          <button
            className="btn btn-primary"
            onClick={handleImport}
            disabled={!importFile || !!loading}
          >
            {loading === 'import' ? 'Importing...' : 'Import'}
          </button>
        </div>

        {importMode === 'overwrite' && (
          <p style={{ fontSize: '12px', color: 'var(--danger)', marginTop: '12px' }}>
            Warning: Overwrite mode will delete ALL existing users, matches, and messages before importing.
          </p>
        )}
      </div>

      {/* Clear Seed Users */}
      <div style={{ ...styles.section, ...styles.dangerSection }}>
        <h2 style={styles.sectionTitle}>Clear Seed Users</h2>
        <p style={styles.sectionDesc}>
          Remove all test/seed users (emails ending in @novadate-test.com) and their associated data.
          Real user accounts will not be affected.
        </p>
        <button
          className="btn btn-secondary"
          style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}
          onClick={handleClearSeed}
          disabled={!!loading}
        >
          {loading === 'clear' ? 'Clearing...' : 'Clear All Seed Users'}
        </button>
      </div>

      {/* Activity Log */}
      {logs.length > 0 && (
        <div style={styles.section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <h2 style={styles.sectionTitle}>Activity Log</h2>
            <button
              className="btn btn-secondary"
              style={{ fontSize: '11px', padding: '4px 10px' }}
              onClick={() => setLogs([])}
            >
              Clear
            </button>
          </div>
          <div style={styles.log}>
            {logs.map((log, i) => (
              <div
                key={i}
                style={
                  log.type === 'success'
                    ? styles.logSuccess
                    : log.type === 'error'
                    ? styles.logError
                    : styles.logInfo
                }
              >
                [{log.time}] {log.message}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
